import PropTypes from 'prop-types';
import { memo } from 'react';

import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography,
} from '@mui/material';

const SupportEmailLink = () => <Link href="mailto:support@questri.ai">support@questri.ai</Link>;

const BillingAndPricingFaq = ({ includeOrgFaq }) => (
  <Box
    sx={{
      '& .MuiAccordionSummary-root': { minHeight: 52 },
      '& .MuiAccordionSummary-root .MuiTypography-root': {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: 0,
      },
      '& .MuiAccordionDetails-root': {
        pt: 0,
        pb: 2,
      },
      '& .MuiAccordionDetails-root > .MuiTypography-root': {
        fontSize: 15,
        lineHeight: 1.6,
        letterSpacing: 0,
        maxWidth: '72ch',
        mb: 1.5,
      },
      '& .MuiAccordionDetails-root > .MuiTypography-root:last-child': { mb: 0 },
    }}
  >
    {
      includeOrgFaq
      && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Typography variant="subtitle1" component="div">
              Do I need a credit card to start?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              No. Your new Questri Barn starts with a 14-day free trial of your selected plan.
              You do not need to provide a credit card or other billing information to get started.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )
    }

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="subtitle1" component="div">
          What happens when my free trial ends?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          To keep access, add a payment method before your trial ends. In your Barn&#39;s billing settings,
          select &#34;View Invoices &amp; Manage Billing Information&#34; to open the secure Stripe portal.
          Your selected plan begins paid billing when the trial ends, unless you cancel or change it first.
        </Typography>
        <Typography paragraph>
          Without a payment method, the trial subscription ends and you lose access to its features.
          If your trial has already ended, select &#34;Subscribe&#34; in billing settings to choose a paid plan.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="subtitle1" component="div">
          What is the difference between Questri Pro and Pay Per Horse?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Both plans include horse records, task planning, the Digital Barn Board, and calendar scheduling.
          Pay Per Horse charges for the horses you manage, up to 50, and includes four staff accounts, including yours.
        </Typography>
        <Typography paragraph>
          Questri Pro includes all Pay Per Horse features, 100 horses, unlimited staff accounts,
          client mobile access, team chat, and onboarding, setup, and priority support.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="subtitle1" component="div">
          What payment methods can I use?
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Questri accepts major credit cards and bank ACH direct debit.
          Available payment options are shown in the secure billing portal.
        </Typography>
        <Typography paragraph>
          Payment by check may be available for invoices above $2,500.
          To request this option, email Questri support at <SupportEmailLink />.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="subtitle1" component="div">
          Can I change my plan or billing frequency later?
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Typography paragraph>
          For an active subscription, select &#34;Modify Subscription&#34; in your Barn&#39;s billing settings
          to see the available plans and billing options in the secure Stripe portal.
          Review the amount and effective date before confirming a change.
        </Typography>
        <Typography paragraph>
          Changing your plan during the free trial can end the trial and start paid billing immediately.
          If you need help choosing or changing plans, email Questri support at <SupportEmailLink />.
        </Typography>
      </AccordionDetails>
    </Accordion>

    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="subtitle1" component="div">
          How do monthly and annual billing work?
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        <Typography paragraph>
          Monthly plans are billed once a month. Annual plans are billed for the full year at the start of each yearly billing period.
          The monthly equivalent shown for an annual plan is for comparison; it is not a monthly installment.
        </Typography>
        <Typography paragraph>
          The amount depends on your selected plan and, for Pay Per Horse, the number of horses you manage.
          Your subscription renews on its selected schedule until canceled.
        </Typography>
        <Typography paragraph>
          To view invoices or update payment details, select &#34;View Invoices &amp; Manage Billing Information&#34;
          in your Barn&#39;s billing settings.
        </Typography>
      </AccordionDetails>
    </Accordion>

    {
      includeOrgFaq
      && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Typography variant="subtitle1" component="div">
              How do I cancel my subscription?
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }}>
            <Typography paragraph>
              Select &#34;Cancel Subscription&#34; in your Barn&#39;s billing settings to open the secure Stripe portal.
              Cancellation currently takes effect immediately and ends access to your subscribed features.
              Review the cancellation details and any billing adjustments before confirming.
            </Typography>
            <Typography paragraph>
              If you have any additional subscriptions, cancel those separately if you no longer need them.
            </Typography>
            <Typography paragraph>
              For help with cancellation, unused time, or resubscribing, email Questri support at <SupportEmailLink />.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )
    }
  </Box>
);

BillingAndPricingFaq.propTypes = { includeOrgFaq: PropTypes.bool };

BillingAndPricingFaq.defaultProps = { includeOrgFaq: false };

export default memo(BillingAndPricingFaq);
